import React, { useState, useEffect } from 'react';
import { buildPropertyPhotoFilename, downloadImageUrl } from '../../utils/downloadImage';

const PhotoLightbox = ({ photos, startIndex = 0, title = "", onClose }) => {
  const safe = (photos || []).filter(Boolean);
  const [index, setIndex] = useState(() => Math.min(Math.max(0, startIndex), Math.max(0, safe.length - 1)));
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    setIndex(Math.min(Math.max(0, startIndex), Math.max(0, safe.length - 1)));
  }, [startIndex, safe.length]);

  useEffect(() => {
    if (!safe.length) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") setIndex((i) => (i - 1 + safe.length) % safe.length);
      if (e.key === "ArrowRight") setIndex((i) => (i + 1) % safe.length);
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [safe.length, onClose]);

  if (!safe.length) return null;

  const go = (dir) => setIndex((i) => (i + dir + safe.length) % safe.length);
  const currentUrl = safe[index];

  const handleDownload = async (e) => {
    e.stopPropagation();
    if (downloading) return;
    setDownloading(true);
    try {
      await downloadImageUrl(currentUrl, buildPropertyPhotoFilename(title, index, currentUrl));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div
      className="photo-lightbox-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Photo viewer"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="photo-lightbox-toolbar">
        <button
          type="button"
          className="photo-lightbox-action"
          onClick={handleDownload}
          disabled={downloading}
          aria-label="Download photo"
          title="Download photo"
        >
          <i className={`fas ${downloading ? 'fa-spinner fa-spin' : 'fa-download'}`} aria-hidden="true"></i>
          <span className="photo-lightbox-action-label">Download</span>
        </button>
        <button type="button" className="photo-lightbox-close" onClick={onClose} aria-label="Close">&times;</button>
      </div>

      <div className="photo-lightbox-stage">
        {safe.length > 1 && (
          <>
            <button type="button" className="gallery-nav prev photo-lightbox-nav" onClick={() => go(-1)} aria-label="Previous photo">
              <i className="fas fa-chevron-left"></i>
            </button>
            <button type="button" className="gallery-nav next photo-lightbox-nav" onClick={() => go(1)} aria-label="Next photo">
              <i className="fas fa-chevron-right"></i>
            </button>
          </>
        )}
        <img
          className="photo-lightbox-img"
          src={currentUrl}
          alt={title ? `${title} — photo ${index + 1}` : `Photo ${index + 1}`}
          draggable={false}
        />
      </div>

      <div className="photo-lightbox-caption">
        <span className="photo-lightbox-counter">{index + 1} / {safe.length}</span>
        {title ? <span>{title}</span> : null}
      </div>
    </div>
  );
};

export default PhotoLightbox;
